export type NinoRegion = {
  id: string;
  name: string;
  lat: number; // sample point (center)
  lon: number;
  bounds: [[number, number], [number, number]]; // [[south,west],[north,east]] for rectangle
  // 1981-2010 monthly climatology (°C), Jan..Dec — approx values
  climatology: number[];
  description: string;
};

export const ninoRegions: NinoRegion[] = [
  {
    id: "nino12", name: "Niño 1+2",
    lat: -5, lon: -85,
    bounds: [[-10, -90], [0, -80]],
    climatology: [25.4, 26.0, 26.4, 25.6, 24.0, 22.5, 21.4, 20.6, 20.5, 20.9, 22.0, 23.7],
    description: "Coastal South America — first to feel El Niño warming.",
  },
  {
    id: "nino3", name: "Niño 3",
    lat: 0, lon: -120,
    bounds: [[-5, -150], [5, -90]],
    climatology: [26.0, 26.8, 27.4, 27.5, 27.0, 26.5, 25.8, 25.2, 25.2, 25.4, 25.6, 25.8],
    description: "Eastern equatorial Pacific.",
  },
  {
    id: "nino34", name: "Niño 3.4 (key index)",
    lat: 0, lon: -150,
    bounds: [[-5, -170], [5, -120]],
    climatology: [26.4, 26.7, 27.2, 27.7, 27.7, 27.4, 27.0, 26.5, 26.5, 26.6, 26.6, 26.5],
    description: "Primary ENSO monitoring region used by NOAA.",
  },
  {
    id: "nino4", name: "Niño 4",
    lat: 0, lon: -175,
    bounds: [[-5, 160], [5, -150]],
    climatology: [28.3, 28.5, 28.8, 29.0, 29.0, 28.8, 28.5, 28.4, 28.5, 28.6, 28.6, 28.4],
    description: "Central / west-central Pacific.",
  },
];

export type SSTReading = {
  region: NinoRegion;
  sst: number;
  anomaly: number;
};

export async function fetchNinoSSTs(): Promise<SSTReading[]> {
  const month = new Date().getUTCMonth();
  const results = await Promise.all(
    ninoRegions.map(async (r) => {
      const lon = ((r.lon + 180) % 360) - 180;
      const url = `https://marine-api.open-meteo.com/v1/marine?latitude=${r.lat}&longitude=${lon}&current=sea_surface_temperature`;
      const res = await fetch(url).then((x) => x.json());
      const sst = res?.current?.sea_surface_temperature ?? 0;
      const clim = r.climatology[month];
      return { region: r, sst, anomaly: sst - clim };
    })
  );
  return results;
}

export function ensoStatus(nino34Anomaly: number) {
  if (nino34Anomaly >= 1.5) return { label: "Strong El Niño", tone: "hot" as const };
  if (nino34Anomaly >= 0.5) return { label: "El Niño", tone: "warm" as const };
  if (nino34Anomaly <= -1.5) return { label: "Strong La Niña", tone: "cold" as const };
  if (nino34Anomaly <= -0.5) return { label: "La Niña", tone: "cool" as const };
  return { label: "ENSO-Neutral", tone: "neutral" as const };
}

export function anomalyColor(a: number): string {
  // returns HSL string
  if (a >= 1.5) return "hsl(0 80% 50%)";
  if (a >= 0.5) return "hsl(18 90% 55%)";
  if (a >= -0.5) return "hsl(45 80% 55%)";
  if (a >= -1.5) return "hsl(200 75% 55%)";
  return "hsl(220 80% 45%)";
}
